import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import { FaPlus, FaSatelliteDish, FaSync, FaLink, FaUnlink, FaMobileAlt, FaCheck, FaTimes, FaCopy, FaInfoCircle, FaTerminal, FaTrash } from 'react-icons/fa';
import '../styles/Pages.css';
import { GetFromApi, PostToApi, PutToApi } from '../utils/api';
import socketService from '../utils/socket';

const GpsDevices = () => {
  const [devices, setDevices] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [formData, setFormData] = useState({ imei: '', device_id: '', vehicle_id: '' });
  const [receiverStatus, setReceiverStatus] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Live Data Logs
  const [dataLogs, setDataLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(true);
  const logsEndRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const response = await GetFromApi('/gps/devices');
      if (response.success) {
        setDevices(response.data);
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await GetFromApi('/vehicles');
      if (response.success) {
        setVehicles(response.data);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const checkReceiverStatus = async () => {
    try {
      const response = await GetFromApi('/gps/health');
      setReceiverStatus(response);
    } catch (error) {
      setReceiverStatus({ success: false, message: 'Receiver offline' });
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await GetFromApi('/gps/logs');
      if (response.success) {
        setDataLogs(response.data);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  useEffect(() => {
    fetchDevices();
    fetchVehicles();
    checkReceiverStatus();
    fetchLogs();

    // Listen for real-time device updates
    socketService.connect();
    socketService.on('vehicle:location', (data) => {
      setDevices(prev => prev.map(d => {
        if (d.device_id === data.deviceId || d.imei === data.deviceId) {
          return { ...d, lat: data.lat, lng: data.lng, speed: data.speed, updated_at: new Date().toISOString() };
        }
        return d;
      }));
    });

    // Listen for GPS data logs
    socketService.on('gps:log', (logEntry) => {
      setDataLogs(prev => [logEntry, ...prev].slice(0, 100));
    });

    return () => {
      socketService.off('vehicle:location');
      socketService.off('gps:log');
    };
  }, []);

  const handleAddDevice = async (e) => {
    e.preventDefault();
    try {
      const response = await PostToApi('/vehicles', {
        number: `GPS-${formData.imei.slice(-6)}`,
        imei: formData.imei,
        device_id: formData.device_id || formData.imei,
        status: 'maintenance'
      });
      if (response.success) {
        setShowAddModal(false);
        setFormData({ imei: '', device_id: '', vehicle_id: '' });
        fetchDevices();
      }
    } catch (error) {
      console.error('Error adding device:', error);
    }
  };

  const handleLinkDevice = async (e) => {
    e.preventDefault();
    if (!selectedDevice || !formData.vehicle_id) return;
    try {
      await PutToApi(`/vehicles/${formData.vehicle_id}`, {
        imei: selectedDevice.imei,
        device_id: selectedDevice.device_id
      });
      setShowLinkModal(false);
      setSelectedDevice(null);
      setFormData({ imei: '', device_id: '', vehicle_id: '' });
      fetchDevices();
      fetchVehicles();
    } catch (error) {
      console.error('Error linking device:', error);
    }
  };

  const handleUnlinkDevice = async (device) => {
    try {
      await PutToApi(`/vehicles/${device.id}`, {
        imei: null,
        device_id: null
      });
      fetchDevices();
    } catch (error) {
      console.error('Error unlinking device:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const clearLogs = () => {
    setDataLogs([]);
  };

  const getStatusBadge = (device) => {
    const lastUpdate = device.updated_at ? new Date(device.updated_at) : null;
    const now = new Date();
    const diffMins = lastUpdate ? (now - lastUpdate) / 60000 : Infinity;

    if (diffMins < 2) return <Badge bg="success">Online</Badge>;
    if (diffMins < 10) return <Badge bg="warning">Idle</Badge>;
    return <Badge bg="danger">Offline</Badge>;
  };

  const getLogStatusBadge = (status) => {
    switch (status) {
      case 'saved': return <Badge bg="success">Saved</Badge>;
      case 'parsed': return <Badge bg="info">Parsed</Badge>;
      case 'received': return <Badge bg="primary">Received</Badge>;
      case 'invalid': return <Badge bg="warning">Invalid</Badge>;
      case 'error': return <Badge bg="danger">Error</Badge>;
      default: return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const getTimeSince = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffSec = Math.floor((now - date) / 1000);
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  const formatLogTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  return (
    <Container fluid className="p-3">
      {/* Header */}
      <div className="page-header">
        <h4 className="page-title">
          <FaSatelliteDish className="me-2" />
          GPS Device Management
        </h4>
        <div className="d-flex gap-2">
          <Button variant="light" onClick={() => { fetchDevices(); checkReceiverStatus(); fetchLogs(); }} disabled={loading}>
            {loading ? <Spinner size="sm" /> : <FaSync />}
          </Button>
          <Button variant="info" onClick={() => setShowSetupModal(true)}>
            <FaInfoCircle className="me-2" /> Setup Guide
          </Button>
          <Button variant="primary" onClick={() => setShowAddModal(true)}>
            <FaPlus className="me-2" /> Add Device
          </Button>
        </div>
      </div>

      {/* Receiver Status */}
      <Row className="mb-4">
        <Col>
          <Alert variant={receiverStatus?.success ? 'success' : 'warning'} className="d-flex align-items-center justify-content-between">
            <div>
              <strong>GPS Receiver Status:</strong>{' '}
              {receiverStatus?.success ? (
                <><FaCheck className="text-success me-1" /> Active - {receiverStatus.activeDevices || 0} devices, {receiverStatus.recentLogs || 0} logs</>
              ) : (
                <><FaTimes className="text-danger me-1" /> Offline or unreachable</>
              )}
            </div>
            <div>
              <strong>Endpoint:</strong>{' '}
              <code className="bg-light px-2 py-1 rounded">{API_URL}/api/gps/receive</code>
              <Button variant="outline-secondary" size="sm" className="ms-2" onClick={() => copyToClipboard(`${API_URL}/api/gps/receive`)}>
                <FaCopy /> {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </Alert>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <h3 className="text-primary mb-0">{devices.length}</h3>
              <small className="text-muted">Total Devices</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <h3 className="text-success mb-0">
                {devices.filter(d => {
                  const lastUpdate = d.updated_at ? new Date(d.updated_at) : null;
                  return lastUpdate && (new Date() - lastUpdate) < 120000;
                }).length}
              </h3>
              <small className="text-muted">Online</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <h3 className="text-warning mb-0">
                {devices.filter(d => d.imei && !d.number.startsWith('GPS-')).length}
              </h3>
              <small className="text-muted">Linked to Vehicles</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <h3 className="text-info mb-0">{dataLogs.length}</h3>
              <small className="text-muted">Data Logs</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Devices Table */}
        <Col lg={7}>
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center">
              <h6 className="mb-0 fw-semibold">Registered GPS Devices</h6>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : devices.length === 0 ? (
                <div className="text-center py-5">
                  <FaSatelliteDish size={50} className="text-muted mb-3" />
                  <h5>No GPS devices registered</h5>
                  <p className="text-muted">Click "Add Device" to register your GPS tracker</p>
                </div>
              ) : (
                <Table striped hover responsive className="mb-0" size="sm">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>IMEI / Device ID</th>
                      <th>Vehicle</th>
                      <th>Speed</th>
                      <th>Last Update</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map(device => (
                      <tr key={device.id}>
                        <td>{getStatusBadge(device)}</td>
                        <td>
                          <div><strong>{device.imei || device.device_id || '-'}</strong></div>
                        </td>
                        <td>
                          {device.number.startsWith('GPS-') ? (
                            <Badge bg="secondary">Unlinked</Badge>
                          ) : (
                            <Badge bg="primary">{device.number}</Badge>
                          )}
                        </td>
                        <td>{device.speed || 0} km/h</td>
                        <td>{getTimeSince(device.updated_at)}</td>
                        <td>
                          {device.number.startsWith('GPS-') ? (
                            <Button size="sm" variant="outline-primary" onClick={() => { setSelectedDevice(device); setShowLinkModal(true); }}>
                              <FaLink />
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline-danger" onClick={() => handleUnlinkDevice(device)}>
                              <FaUnlink />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Live Data Logs */}
        <Col lg={5}>
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center">
              <h6 className="mb-0 fw-semibold">
                <FaTerminal className="me-2" />
                Live Data Logs
                {dataLogs.length > 0 && <Badge bg="success" className="ms-2">{dataLogs.length}</Badge>}
              </h6>
              <div>
                <Button size="sm" variant="outline-light" className="me-2" onClick={() => setShowLogs(!showLogs)}>
                  {showLogs ? 'Hide' : 'Show'}
                </Button>
                <Button size="sm" variant="outline-danger" onClick={clearLogs}>
                  <FaTrash />
                </Button>
              </div>
            </Card.Header>
            {showLogs && (
              <Card.Body 
                className="p-0" 
                style={{ 
                  maxHeight: '400px', 
                  overflowY: 'auto', 
                  backgroundColor: '#1e1e1e',
                  fontFamily: 'monospace',
                  fontSize: '12px'
                }}
              >
                {dataLogs.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <FaTerminal size={30} className="mb-2" style={{ color: '#666' }} />
                    <p style={{ color: '#888' }}>Waiting for GPS data...</p>
                    <small style={{ color: '#666' }}>Data will appear here when your GPS device sends data</small>
                  </div>
                ) : (
                  <div className="p-2">
                    {dataLogs.map((log, index) => (
                      <div 
                        key={log.id || index} 
                        className="mb-2 p-2 rounded"
                        style={{ 
                          backgroundColor: log.status === 'error' ? '#3d1515' : 
                                          log.status === 'invalid' ? '#3d3415' : '#2d2d2d',
                          borderLeft: `3px solid ${
                            log.status === 'saved' ? '#28a745' :
                            log.status === 'error' ? '#dc3545' :
                            log.status === 'invalid' ? '#ffc107' : '#17a2b8'
                          }`
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <span style={{ color: '#17a2b8' }}>{formatLogTime(log.timestamp)}</span>
                          {getLogStatusBadge(log.status)}
                        </div>
                        {log.deviceId && (
                          <div style={{ color: '#28a745' }}>
                            📱 Device: {log.deviceId}
                          </div>
                        )}
                        {log.lat && log.lng && (
                          <div style={{ color: '#ffc107' }}>
                            📍 Location: {log.lat.toFixed(4)}, {log.lng.toFixed(4)} | Speed: {log.speed || 0} km/h
                          </div>
                        )}
                        {log.error && (
                          <div style={{ color: '#dc3545' }}>
                            ❌ Error: {log.error}
                          </div>
                        )}
                        {log.newDevice && (
                          <div style={{ color: '#28a745' }}>
                            🆕 New device auto-registered!
                          </div>
                        )}
                        <div style={{ color: '#666', fontSize: '10px', marginTop: '4px' }}>
                          IP: {log.ip || 'unknown'} | Content-Type: {log.contentType || 'unknown'}
                        </div>
                      </div>
                    ))}
                    <div ref={logsEndRef} />
                  </div>
                )}
              </Card.Body>
            )}
          </Card>
        </Col>
      </Row>

      {/* Add Device Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add GPS Device</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddDevice}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>IMEI Number *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter 15-digit IMEI"
                value={formData.imei}
                onChange={e => setFormData({ ...formData, imei: e.target.value })}
                required
                maxLength={20}
              />
              <Form.Text className="text-muted">
                Find this on your GPS device or its packaging
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Device ID (Optional)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Alternative device identifier"
                value={formData.device_id}
                onChange={e => setFormData({ ...formData, device_id: e.target.value })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit">Add Device</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Link Device Modal */}
      <Modal show={showLinkModal} onHide={() => setShowLinkModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Link Device to Vehicle</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleLinkDevice}>
          <Modal.Body>
            <Alert variant="info">
              <strong>Device:</strong> {selectedDevice?.imei || selectedDevice?.device_id}
            </Alert>
            <Form.Group className="mb-3">
              <Form.Label>Select Vehicle</Form.Label>
              <Form.Select
                value={formData.vehicle_id}
                onChange={e => setFormData({ ...formData, vehicle_id: e.target.value })}
                required
              >
                <option value="">Choose a vehicle...</option>
                {vehicles.filter(v => !v.imei).map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id}>{vehicle.number}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowLinkModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit">Link Device</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Setup Guide Modal */}
      <Modal show={showSetupModal} onHide={() => setShowSetupModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title><FaMobileAlt className="me-2" /> GPS Device Setup Guide</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h6>Step 1: Configure your GPS device via SMS</h6>
          <p>Send these SMS commands to your GPS tracker's phone number:</p>
          
          <Card className="mb-3 bg-light">
            <Card.Body>
              <code className="d-block mb-2">1. Set APN: <strong>APN,your_sim_apn#</strong></code>
              <code className="d-block mb-2">2. Set Server: <strong>NIP,{window.location.hostname},5000,0#</strong></code>
              <code className="d-block mb-2">3. Set Interval: <strong>PRD,5#</strong> (every 5 seconds)</code>
              <code className="d-block">4. Enable HTTP Mode: <strong>PROTOCOL,JSON#</strong></code>
            </Card.Body>
          </Card>

          <h6>Step 2: Configure Data Endpoint</h6>
          <p>Your GPS device should POST data to:</p>
          <Card className="mb-3 bg-light">
            <Card.Body>
              <code>{API_URL}/api/gps/receive</code>
              <Button 
                size="sm" 
                variant="outline-primary" 
                className="ms-2"
                onClick={() => copyToClipboard(`${API_URL}/api/gps/receive`)}
              >
                <FaCopy /> Copy
              </Button>
            </Card.Body>
          </Card>

          <h6>Step 3: Expected Data Format (VTS Protocol v2.2)</h6>
          <pre className="bg-dark text-light p-3 rounded">
{`{
  "uid": "IMEI_NUMBER",
  "gps": {
    "loc": [23.0225, 72.5714],
    "speed": 45,
    "dir": 180
  },
  "info": {
    "dt": "2025-12-23T10:30:00Z"
  }
}`}
          </pre>

          <Alert variant="success">
            <strong>Tip:</strong> Once configured, the device will automatically start sending data. 
            Watch the "Live Data Logs" panel to see incoming data!
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowSetupModal(false)}>Got it!</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default GpsDevices;
